import { useEffect, type JSX } from 'react';
import { BellIcon, XIcon } from 'lucide-react';
import type { Notification } from '@kennisbank/shared';

/**
 * Meldingsstrook rechtsboven: het WhatsApp-plopje zonder geluid (Linda,
 * 3 sep 2026). Toont alleen de titel van de melding, nooit inhoud; de bel
 * blijft de blijvende plek. Verdwijnt na acht seconden of met het kruisje.
 */

interface MeldingStrookProps {
  items: Notification[];
  onOpen: (melding: Notification) => void;
  onSluit: (id: string) => void;
}

const ZICHTBAAR_MS = 8_000;

function Strookje({
  melding,
  onOpen,
  onSluit,
}: {
  melding: Notification;
  onOpen: (m: Notification) => void;
  onSluit: (id: string) => void;
}): JSX.Element {
  useEffect(() => {
    const t = setTimeout(() => onSluit(melding.id), ZICHTBAAR_MS);
    return () => clearTimeout(t);
  }, [melding.id, onSluit]);

  return (
    <div
      role="status"
      className="pointer-events-auto flex w-[min(20rem,calc(100vw-1.5rem))] items-start gap-2.5 rounded-lg border border-brand-100 bg-white p-3 shadow-lg"
    >
      <BellIcon size={16} strokeWidth={2} className="mt-0.5 shrink-0 text-brand-600" />
      <button
        type="button"
        onClick={() => onOpen(melding)}
        className="min-w-0 flex-1 text-left text-[13px] font-semibold leading-snug text-ink-900 hover:underline"
      >
        {melding.title}
        {!!melding.count && melding.count > 1 && (
          <span className="ml-1.5 font-normal text-ink-900/55">({melding.count})</span>
        )}
      </button>
      <button
        type="button"
        onClick={() => onSluit(melding.id)}
        aria-label="Melding sluiten"
        className="shrink-0 rounded-md p-0.5 text-ink-900/45 hover:bg-brand-50 hover:text-ink-900"
      >
        <XIcon size={14} />
      </button>
    </div>
  );
}

export function MeldingStrook({ items, onOpen, onSluit }: MeldingStrookProps): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="pointer-events-none fixed right-3 top-16 z-50 flex flex-col gap-2">
      {items.map((melding) => (
        <Strookje key={melding.id} melding={melding} onOpen={onOpen} onSluit={onSluit} />
      ))}
    </div>
  );
}
